import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function Settings() {
  const { company, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    api.get("/company").then((r) => setForm({
      name: "", owner_name: "", phone: "", whatsapp: "", email: "", city: "", state: "",
      doc: "", address: "", settings: {}, ...r.data,
    })).catch(() => {});
  }, []);

  if (!form) return <div className="py-20 text-center text-slate-400">Carregando...</div>;

  const s = form.settings || {};
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => setForm({ ...form, settings: { ...s, [k]: Number(e.target.value) || 0 } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", { ...form, onboarding_done: true });
      await refresh();
      toast.success("Configurações salvas!");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const exportData = async () => {
    const { data } = await api.get("/company/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "gepr-meus-dados.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("context", "logo");
      const { data } = await api.post("/files/upload", fd);
      setForm({ ...form, logo_url: data.url });
      toast.success("Logo enviada! Clique em Salvar configurações para concluir.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="space-y-5 animate-in-up max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Configurações</h1>
        <p className="text-sm text-slate-500">Dados da empresa e motor de precificação</p>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Dados da empresa</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5"><Label>Nome da empresa</Label><Input className="h-12" data-testid="set-name" value={form.name} onChange={set("name")} /></div>
          <div className="space-y-1.5"><Label>Responsável</Label><Input className="h-12" data-testid="set-owner" value={form.owner_name} onChange={set("owner_name")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Telefone</Label><Input className="h-12" data-testid="set-phone" value={form.phone} onChange={set("phone")} /></div>
            <div className="space-y-1.5"><Label>WhatsApp</Label><Input className="h-12" data-testid="set-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} /></div>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="h-5 w-5 accent-sky-600" data-testid="set-whatsapp-notify"
                   checked={!!form.whatsapp_notifications}
                   onChange={(e) => setForm({ ...form, whatsapp_notifications: e.target.checked })} />
            Quero receber as notificações (manutenções vencendo, trial, follow-up) também por WhatsApp
          </label>
          <div className="space-y-1.5"><Label>E-mail comercial</Label><Input className="h-12" data-testid="set-email" value={form.email} onChange={set("email")} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>Cidade</Label><Input className="h-12" data-testid="set-city" value={form.city} onChange={set("city")} /></div>
            <div className="space-y-1.5"><Label>UF</Label><Input className="h-12" maxLength={2} data-testid="set-state" value={form.state} onChange={set("state")} /></div>
          </div>
          <div className="space-y-1.5"><Label>CNPJ/CPF</Label><Input className="h-12" data-testid="set-doc" value={form.doc} onChange={set("doc")} /></div>
          <div className="space-y-1.5"><Label>Endereço</Label><Input className="h-12" data-testid="set-address" value={form.address} onChange={set("address")} /></div>
          <div className="space-y-1.5">
            <Label>Logo da empresa (aparece nos PDFs)</Label>
            <div className="flex items-center gap-3">
              {form.logo_url && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="Logo" className="h-14 w-14 rounded-lg object-contain border border-slate-200 bg-white" data-testid="set-logo-preview" />
              )}
              <Input type="file" accept="image/*" className="h-12" onChange={uploadLogo} disabled={uploadingLogo} data-testid="set-logo-input" />
            </div>
            <p className="text-[11px] text-slate-400">{uploadingLogo ? "Enviando..." : "PNG ou JPG até 10 MB."}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Motor de precificação</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Hora técnica (R$)</Label><Input type="number" step="0.01" className="h-12 font-mono-num" data-testid="set-hora" value={s.valor_hora ?? 0} onChange={setNum("valor_hora")} /></div>
          <div className="space-y-1.5"><Label>Valor por km (R$)</Label><Input type="number" step="0.01" className="h-12 font-mono-num" data-testid="set-km" value={s.valor_km ?? 0} onChange={setNum("valor_km")} /></div>
          <div className="space-y-1.5"><Label>Impostos (%)</Label><Input type="number" step="0.1" className="h-12 font-mono-num" data-testid="set-impostos" value={s.impostos_pct ?? 0} onChange={setNum("impostos_pct")} /></div>
          <div className="space-y-1.5"><Label>Margem desejada (%)</Label><Input type="number" className="h-12 font-mono-num" data-testid="set-margem" value={s.margem_pct ?? 0} onChange={setNum("margem_pct")} /></div>
          <div className="space-y-1.5"><Label>Deslocamento mínimo (R$)</Label><Input type="number" step="0.01" className="h-12 font-mono-num" data-testid="set-desl-min" value={s.deslocamento_minimo ?? 0} onChange={setNum("deslocamento_minimo")} /></div>
          <div className="space-y-1.5"><Label>Valor mínimo de serviço (R$)</Label><Input type="number" step="0.01" className="h-12 font-mono-num" data-testid="set-min-servico" value={s.valor_minimo_servico ?? 0} onChange={setNum("valor_minimo_servico")} /></div>
          <div className="space-y-1.5"><Label>Adicional urgência (%)</Label><Input type="number" className="h-12 font-mono-num" data-testid="set-urgencia" value={s.adicional_urgencia ?? 0} onChange={setNum("adicional_urgencia")} /></div>
          <div className="space-y-1.5"><Label>Adicional noturno/plantão (%)</Label><Input type="number" className="h-12 font-mono-num" data-testid="set-noturno" value={s.adicional_noturno ?? 0} onChange={setNum("adicional_noturno")} /></div>
          <div className="space-y-1.5"><Label>Desconto máximo (%)</Label><Input type="number" className="h-12 font-mono-num" data-testid="set-desc-max" value={s.desconto_max_pct ?? 0} onChange={setNum("desconto_max_pct")} /></div>
          <div className="space-y-1.5"><Label>Margem mínima (%)</Label><Input type="number" className="h-12 font-mono-num" data-testid="set-margem-min" value={s.margem_minima_pct ?? 0} onChange={setNum("margem_minima_pct")} /></div>
        </CardContent>
      </Card>

      <Button className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={save} disabled={saving} data-testid="settings-save-btn">
        <Save className="h-4 w-4 mr-2" /> {saving ? "Salvando..." : "Salvar configurações"}
      </Button>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Seus dados (LGPD)</CardTitle></CardHeader>
        <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm text-slate-600">Exporte todos os seus dados (clientes, orçamentos, OS, equipamentos) em JSON.</p>
          <Button variant="outline" className="h-11 shrink-0" onClick={exportData} data-testid="export-data-btn">
            <Download className="h-4 w-4 mr-2" /> Exportar dados
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
